import { cloneElement, ReactNode, useMemo, useRef } from 'react'
import Float from './Float'
import { AnimatePresence, motion } from 'framer-motion'
import {
  useFloating,
  arrow as fArrow,
  Placement as FPlacement,
  offset,
  flip,
  shift,
  autoUpdate,
  hide,
  size
} from '@floating-ui/react-dom'
import cn from 'classnames'

export interface PopoverProps {
  position?:
    | 'top-left'
    | 'top-middle'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-middle'
    | 'bottom-right'
    | 'left-top'
    | 'left-middle'
    | 'left-bottom'
    | 'right-top'
    | 'right-middle'
    | 'right-bottom'
  children: React.ReactElement
  content: React.ReactNode | React.ReactNode[]
  teleport?: boolean
  show: boolean
  useArrow?: boolean
  arrow?: ReactNode
  matchReferenceWidth?: boolean
  dark?: boolean
}

const containerMotionVariants = {
  hide: {
    opacity: 0,
    y: -10,
    transition: {
      staggerChildren: 0.1,
      when: 'afterChildren'
    }
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.05
    }
  }
}

const Popover = ({
  children,
  content,
  teleport = false,
  show = false,
  position = 'top-middle',
  useArrow = true,
  arrow = null,
  matchReferenceWidth,
  dark = false
}: PopoverProps) => {
  const arrowRef = useRef(null)
  const placement = ({
    'top-left': 'top-start',
    'top-middle': 'top',
    'top-right': 'top-end',
    'bottom-left': 'bottom-start',
    'bottom-middle': 'bottom',
    'bottom-right': 'bottom-end',
    'left-top': 'left-start',
    'left-middle': 'left',
    'left-bottom': 'left-end',
    'right-top': 'right-start',
    'right-middle': 'right',
    'right-bottom': 'right-end'
  }[position] ?? 'top') as FPlacement
  const staticSide =
    {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[placement.split('-')[0]] ?? 'top'

  const { x, y, reference, floating, strategy, middlewareData } = useFloating({
    placement,
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true
      }),
    strategy: teleport ? 'fixed' : 'absolute',
    middleware: [
      offset(useArrow ? 8 : 2),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          if (matchReferenceWidth)
            elements.floating.style.width = rects.reference.width + 'px'
        }
      }),
      fArrow({
        element: arrowRef
      }),
      hide({ strategy: 'referenceHidden' })
    ]
  })

  const clonedChild = useMemo(() => {
    return cloneElement(children, { ref: reference })
  }, [children, reference])

  const hideByMiddleware = middlewareData.hide?.referenceHidden === true

  return (
    <>
      {clonedChild}
      <Float>
        <AnimatePresence initial={false}>
          {show && (
            <motion.div
              ref={floating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0
              }}
              initial="hide"
              animate={hideByMiddleware ? 'hide' : 'show'}
              exit="hide"
              variants={containerMotionVariants}
              className={cn(
                'z-popover',
                hideByMiddleware && 'pointer-events-none'
              )}
            >
              {content}
              {useArrow && (
                <div
                  ref={arrowRef}
                  className="absolute"
                  style={{
                    top: middlewareData.arrow?.y,
                    left: middlewareData.arrow?.x,
                    [staticSide]: -4
                  }}
                >
                  {arrow ?? (
                    <div
                      className={cn([
                        'w-2.5 h-2.5 -rotate-45 bg-white',
                        dark && 'bg-dark text-white'
                      ])}
                    ></div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Float>
    </>
  )
}

export default Popover
